import { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabase';
import { detectInputType, normalizePhone } from '../utils/auth';

/**
 * Shown after login when the user has no first/last name on file, OR is
 * missing one of (email, phone). Triggered for users who signed up via
 * an event link / route share / public flow and skipped onboarding.
 *
 * Captures:
 * - First name (required), last name (optional)
 * - The OTHER contact method (if they signed in with phone, asks for email;
 *   if email, asks for phone). Real-world reason: people sign up the day
 *   before with one method and then try logging in the day-of with the
 *   other and end up with two ghost accounts.
 *
 * @param {{ onComplete: (firstName: string, lastName: string) => void }} props
 */
export default function CollectName({ onComplete }) {
  const { user } = useAuth();

  // What did they sign in with? Anchor the form to the OTHER one.
  const hasEmail = !!user?.email;
  const hasPhone = !!user?.phone;
  const askForEmail = !hasEmail; // they signed in with phone → collect email
  const askForPhone = !hasPhone; // they signed in with email → collect phone
  const askedContactKind = useMemo(() => {
    if (askForEmail) return 'email';
    if (askForPhone) return 'phone';
    return null;
  }, [askForEmail, askForPhone]);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [contact, setContact] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const first = firstName.trim();
    const last = lastName.trim();
    const contactValue = contact.trim();

    if (!first) { setError('Please enter your first name.'); return; }

    if (askedContactKind === 'email') {
      if (!contactValue || !contactValue.includes('@')) {
        setError('Please enter a valid email address.');
        return;
      }
    } else if (askedContactKind === 'phone') {
      const detected = detectInputType(contactValue);
      if (detected !== 'phone') {
        setError('Please enter a valid phone number including country code.');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      // Update or create the public.users row. We use UPSERT because some
      // users (notably those whose handle_new_user trigger didn't fire)
      // have no public.users row yet — UPDATE alone returns 0 rows and
      // silently loses their info.
      const upsertRow = {
        id: user.id,
        email: user.email || null,
        phone_number: user.phone || null,
        first_name: first,
        last_name: last || null,
      };
      if (askedContactKind === 'email') upsertRow.email = contactValue;
      if (askedContactKind === 'phone') upsertRow.phone_number = normalizePhone(contactValue);

      const { error: dbError } = await supabase
        .from('users')
        .upsert(upsertRow, { onConflict: 'id' });
      if (dbError) throw dbError;

      // Also link the new contact into auth.users via the existing RPCs so
      // future sign-ins via either method resolve to the same account. Best
      // effort — failure here doesn't roll back the public.users update.
      try {
        if (askedContactKind === 'email') {
          await supabase.rpc('link_email_to_user', { p_email: contactValue });
        } else if (askedContactKind === 'phone') {
          await supabase.rpc('link_phone_to_user', { p_phone: normalizePhone(contactValue) });
        }
      } catch (linkErr) {
        console.warn('Auth link skipped:', linkErr?.message);
      }

      onComplete(first, last);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = firstName.trim().length > 0 && !loading
    && (askedContactKind === null || contact.trim().length > 0);

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ fontSize: '3.5rem', marginBottom: 16, textAlign: 'center' }}>👋</div>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12, textAlign: 'center' }}>
          Welcome!
        </h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem', textAlign: 'center', marginBottom: 24, lineHeight: 1.5 }}>
          {askedContactKind
            ? "Help event organizers find you, and add a backup way to log in."
            : "What's your name? This helps event organizers and fellow drivers know who you are."}
        </p>

        {error && (
          <div style={errorStyle}>{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 12 }}>
            <input
              type="text" value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name" required autoFocus autoComplete="given-name"
              className="form-input" style={inputStyle}
            />
          </div>
          <div style={{ marginBottom: askedContactKind ? 12 : 16 }}>
            <input
              type="text" value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last name (optional)"
              autoComplete="family-name"
              className="form-input" style={inputStyle}
            />
          </div>

          {askedContactKind === 'email' && (
            <div style={{ marginBottom: 16 }}>
              <input
                type="email" value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder="Email address" required autoComplete="email"
                inputMode="email"
                className="form-input" style={inputStyle}
              />
              <p style={hintStyle}>
                You signed in with your phone. Adding an email lets you log in either way later.
              </p>
            </div>
          )}
          {askedContactKind === 'phone' && (
            <div style={{ marginBottom: 16 }}>
              <input
                type="tel" value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder="Phone number" required autoComplete="tel"
                inputMode="tel"
                className="form-input" style={inputStyle}
              />
              <p style={hintStyle}>
                You signed in with your email. Adding a phone lets you log in either way later.
              </p>
            </div>
          )}

          <button
            type="submit" disabled={!canSubmit}
            style={{ ...submitStyle, cursor: canSubmit ? 'pointer' : 'not-allowed', opacity: canSubmit ? 1 : 0.6 }}
          >
            {loading ? 'Saving...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}

const containerStyle = {
  minHeight: '100dvh', width: '100%',
  background: 'var(--bg-primary)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};

const cardStyle = {
  maxWidth: 400, width: '100%',
  background: 'var(--bg-elevated)',
  borderRadius: 'var(--radius-md)', padding: 32,
};

const inputStyle = {
  width: '100%', padding: '12px 14px', fontSize: '0.9375rem', boxSizing: 'border-box',
};

const submitStyle = {
  width: '100%', padding: '14px 16px', fontSize: '0.9375rem', fontWeight: 600,
  borderRadius: 'var(--radius-sm)', border: 'none',
  background: 'var(--accent)', color: '#fff', transition: 'opacity 0.2s',
};

const errorStyle = {
  background: 'rgba(211, 71, 74, 0.1)',
  border: '1px solid rgba(211, 71, 74, 0.3)',
  borderRadius: 'var(--radius-sm)',
  padding: '10px 14px', marginBottom: 16,
  fontSize: '0.8125rem', color: 'var(--accent)',
};

const hintStyle = {
  margin: '6px 2px 0',
  fontSize: '0.6875rem',
  color: 'var(--text-muted)',
  lineHeight: 1.4,
};
